import React, { useState, useEffect, useContext, useRef } from "react";
import { makeStyles, Popover, IconButton, List, ListItem, ListItemText, Badge, Typography, Divider } from "@material-ui/core";
import { NotificationsActive as NotificationsActiveIcon } from "@material-ui/icons";
import { AuthContext } from "../../context/Auth/AuthContext";
import Tooltip from "@material-ui/core/Tooltip";
import alertSound from "../../assets/sound.mp3";
import { format, parseISO } from "date-fns";
import { useHistory } from "react-router-dom";

const useStyles = makeStyles(theme => ({
	tabContainer: {
		overflowY: "auto",
		maxHeight: 350,
		...theme.scrollbarStyles,
	},
	popoverPaper: {
		width: "100%",
		maxWidth: 350,
		padding: theme.spacing(1),
	},
	noNotifications: {
		padding: theme.spacing(2),
		textAlign: "center",
		color: theme.palette.text.secondary,
	}
}));

const RemindersPopover = () => {
	const classes = useStyles();
	const history = useHistory();
	const { user, socket } = useContext(AuthContext);
	const [reminders, setReminders] = useState([]);
	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);
	const isMountedRef = useRef(true);
	const reminderAudioRef = useRef(null);

	useEffect(() => {
		isMountedRef.current = true;
		reminderAudioRef.current = new Audio(alertSound);
		reminderAudioRef.current.preload = "auto";

		return () => {
			isMountedRef.current = false;
			if (reminderAudioRef.current) {
				reminderAudioRef.current.pause();
				reminderAudioRef.current.src = "";
				reminderAudioRef.current = null;
			}
		};
	}, []);

	useEffect(() => {
		if (user.id && socket) {
			const reminderChannel = `company-${user.company_id}-reminder`;
			const handleReminder = (data) => {
				if (!isMountedRef.current) return;
				if (data.action === "show") {
					const rec = data.record;
					// Se o lembrete tem destinatário definido, só mostra para ele.
					if (rec?.targetUserId && rec.targetUserId !== user.id) return;
					setReminders(prev => {
						if (prev.some(r => String(r.id) === String(rec.id))) return prev;
						return [rec, ...prev];
					});
					if (reminderAudioRef.current) {
						reminderAudioRef.current.currentTime = 0;
						reminderAudioRef.current.play().catch(() => {});
					}
				}
			};

			socket.on(reminderChannel, handleReminder);
			return () => {
				socket.off(reminderChannel, handleReminder);
			};
		}
	}, [user, socket]);

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleReminderClick = (reminder) => {
		handleClose();
		if (reminder.ticket) {
			history.push(`/tickets/${reminder.ticket.uuid}`);
		} else if (reminder.kind === "crm_task") {
			// follow-up do CRM: leva para o calendário de agendamentos
			history.push('/schedules');
		}
		setReminders(prev => prev.filter(r => String(r.id) !== String(reminder.id)));
	};

	return (
		<>
			<Tooltip title="Lembretes" arrow>
				<IconButton
					onClick={handleClick}
					color="inherit"
					style={{ color: "white", padding: 4 }}
				>
					<Badge badgeContent={reminders.length} color="secondary" overlap="circular">
						<NotificationsActiveIcon style={{ color: reminders.length > 0 ? "red" : "white" }} />
					</Badge>
				</IconButton>
			</Tooltip>
			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				classes={{ paper: classes.popoverPaper }}
			>
				<Typography variant="h6" style={{ padding: 8 }}>Lembretes</Typography>
				<Divider />
				<List className={classes.tabContainer}>
					{reminders.length === 0 ? (
						<Typography className={classes.noNotifications}>Sem novos lembretes.</Typography>
					) : (
						reminders.map((reminder) => (
							<ListItem key={reminder.id} button onClick={() => handleReminderClick(reminder)}>
								<ListItemText
									primary={reminder.note}
									secondary={`${reminder.contact?.name || "Contato"} - ${format(parseISO(reminder.reminderAt), "dd/MM/yyyy HH:mm")}`}
								/>
							</ListItem>
						))
					)}
				</List>
			</Popover>
		</>
	);
};

export default RemindersPopover;
